/*
 * High-Risk Users page.
 *
 * Filtered roster of the heaviest clickers, sortable and CSV-exportable.
 * Department and risk-level filters apply locally on top of any inherited
 * scope.
 *
 * Calculations:
 *   - clickRate = clicked / sent; reportRate = reported / sent.
 *   - risk-level pill from HIGH_RISK_USERS funnel row, not from
 *     USER_SCORES, so the page reflects raw engagement rather than
 *     the weighted score.
 *
 * Postgres mapping: view ordered by (clicks DESC, risk DESC) backed by
 * `simulations_sent` aggregate. CSV export should call a server endpoint
 * that streams the result rather than buffering in the browser.
 *
 * Integration hook: `useHighRiskUsersQuery({ dept, riskLevel, scope })`
 * cache 1h. Export via `useHighRiskUsersCsvQuery` returning a signed URL.
 */
import React, { useState, useMemo } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { USERS, HIGH_RISK_USERS, DATA_EPOCH } from '../data';
import useDashboardData from '../hooks/useDashboardData';
import { riskColor } from '../helpers';
import ActiveFilters from './ActiveFilters';
import DataTable from './DataTable';

const isIn = (val, vs) => { if (!vs) return true; const a = Array.isArray(val) ? val : [val]; return Array.isArray(vs) ? a.some((x) => vs.includes(x)) : a.includes(vs); };

// Lazy + epoch-tracked so the funnel map follows the live hydrated data.
let funnelCache = null;
let funnelEpoch = -1;
const FUNNEL_BY_EMAIL = {
    get(email) {
        if (funnelEpoch !== DATA_EPOCH) {
            funnelCache = new Map(HIGH_RISK_USERS.map((u) => [u.email, u]));
            funnelEpoch = DATA_EPOCH;
        }
        return funnelCache.get(email);
    },
};
const ZERO_FUNNEL = { sent: 0, clicked: 0, secondStage: 0, reported: 0 };

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

function FilterSelect({ value, onChange, options, placeholder }) {
    return (
        <Box
            component="select"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: '4px',
                px: 1.25, py: '6px', fontSize: 11, color: value ? 'text.primary' : 'text.disabled',
                bgcolor: 'background.paper', outline: 'none', cursor: 'pointer', fontFamily: 'inherit',
                '&:focus': { borderColor: 'secondary.main' },
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='8' height='5' viewBox='0 0 8 5' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l3 3 3-3' stroke='%236B7591' stroke-width='1.2' stroke-linecap='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
                pr: 3,
            }}
        >
            <option value="">{placeholder}</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </Box>
    );
}

function exportCSV(rows) {
    const headers = ['Name', 'Email', 'Department', 'Location', 'Risk Level', 'Sent', 'Clicked', '2nd Stage', 'Reported', 'Click Rate', 'Report Rate'];
    const lines = [headers.join(','), ...rows.map((u) => {
        const f = FUNNEL_BY_EMAIL.get(u.email) || ZERO_FUNNEL;
        const clickRate = f.sent ? Math.round((f.clicked / f.sent) * 100) : 0;
        const reportRate = f.sent ? Math.round((f.reported / f.sent) * 100) : 0;
        return [
            `"${u.name}"`, `"${u.email || ''}"`, `"${u.dept}"`, `"${u.location || ''}"`, `"${u.lv}"`,
            f.sent, f.clicked, f.secondStage, f.reported, `"${clickRate}%"`, `"${reportRate}%"`,
        ].join(',');
    })];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'high-risk-users.csv'; a.click();
    URL.revokeObjectURL(url);
}

function LevelBadge({ lv }) {
    const c = riskColor(lv);
    return (
        <Box sx={{ display: 'inline-flex', px: '8px', py: '2px', borderRadius: '999px', fontSize: 10, fontWeight: 600, bgcolor: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
            {lv.charAt(0).toUpperCase() + lv.slice(1)}
        </Box>
    );
}

export default function HighRiskUsersPage({ drill, scope, removeScope }) {
    const [filterDept, setFilterDept] = useState('');
    const [filterLevel, setFilterLevel] = useState('');
    // Uncapped risk-ranked users from the rollup when unscoped; scope filters fall back to the raw roster.
    const scopeActive = !!(scope && Object.keys(scope).some((k) => { const v = scope[k]; return Array.isArray(v) ? v.length > 0 : !!v; }));
    const riskUsersQ = useDashboardData({ metric: 'simulations', aggregation: 'users', limit: 5000 });
    const aggRows = (!scopeActive && Array.isArray(riskUsersQ.data?.riskUsers)) ? riskUsersQ.data.riskUsers.map((u) => ({
        name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email, email: u.email, dept: u.dept || '', location: u.location || '',
        lv: (u.band === 'Critical' || u.band === 'High') ? 'high' : u.band === 'Medium' ? 'med' : 'low',
        sent: u.sent, clicked: u.clicked, secondStage: u.secondStage, reported: u.reported,
        clickRate: u.sent ? Math.round((u.clicked / u.sent) * 100) : 0, reportRate: u.sent ? Math.round((u.reported / u.sent) * 100) : 0,
    })) : null;

    const baseUsers = useMemo(() => {
        const s = scope || {};
        return (aggRows || USERS).filter((u) => {
            if (!isIn(u.dept,     s.department)) return false;
            if (!isIn(u.location, s.location))   return false;
            if (!isIn(u.city,     s.city))       return false;
            if (!isIn(u.country,  s.country))    return false;
            if (!isIn(u.company,  s.company))    return false;
            if (!isIn(u.state,    s.state))      return false;
            if (!isIn(u.adGroups,  s.adGroup))    return false;
            if (s.user) {
                const userVals = Array.isArray(s.user) ? s.user : [s.user];
                if (!userVals.some((v) => u.email === v || u.name === v)) return false;
            }
            return true;
        });
    }, [scope, aggRows]);

    const depts = useMemo(() => [...new Set(baseUsers.map((u) => u.dept))].sort(), [baseUsers]);

    const filtered = useMemo(() => {
        return baseUsers.filter((u) => {
            if (filterDept  && u.dept !== filterDept)   return false;
            if (filterLevel && u.lv   !== filterLevel)  return false;
            return true;
        });
    }, [baseUsers, filterDept, filterLevel]);

    const hasFilters = filterDept || filterLevel;

    // Pre-merge funnel stats into the row so DataTable's accessor +
    // sortable headers can read funnel fields the same way they read
    // any other column.
    const rows = useMemo(() => filtered.map((u) => {
        if (aggRows) return u;
        const f = FUNNEL_BY_EMAIL.get(u.email) || ZERO_FUNNEL;
        const clickRate = f.sent ? Math.round((f.clicked / f.sent) * 100) : 0;
        const reportRate = f.sent ? Math.round((f.reported / f.sent) * 100) : 0;
        return { ...u, ...f, clickRate, reportRate };
    }), [filtered, aggRows]);

    const columns = [
        { id: 'name',       label: 'Name',       always: true,   sortable: true, type: 'string', accessor: (u) => u.name },
        { id: 'email',      label: 'Email',      defaultOn: true,sortable: true, type: 'string', accessor: (u) => u.email },
        { id: 'dept',       label: 'Department', defaultOn: true,sortable: true, type: 'string', accessor: (u) => u.dept },
        { id: 'location',   label: 'Location',   defaultOn: true,sortable: true, type: 'string', accessor: (u) => u.location || '' },
        { id: 'lv',         label: 'Level',      defaultOn: true,sortable: true, type: 'string', accessor: (u) => u.lv, render: (u) => <LevelBadge lv={u.lv} /> },
        { id: 'sent',       label: 'Sent',       defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.sent, align: 'right' },
        { id: 'clicked',    label: 'Clicked',    defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.clicked, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 700, color: u.clicked > 2 ? 'var(--red)' : u.clicked > 0 ? 'var(--amber)' : 'var(--green)' }}>{u.clicked}</Box> },
        { id: 'secondStage',label: '2nd Stage',  defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.secondStage, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 700, color: u.secondStage > 0 ? 'var(--red)' : 'text.secondary' }}>{u.secondStage}</Box> },
        { id: 'reported',   label: 'Reported',   defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.reported, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 700, color: u.reported > 0 ? 'var(--green)' : 'text.secondary' }}>{u.reported}</Box> },
        { id: 'clickRate',  label: 'Click Rate', defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.clickRate, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 600, color: u.clickRate >= 30 ? 'var(--red)' : u.clickRate >= 15 ? 'var(--amber)' : 'var(--green)' }}>{u.clickRate}%</Box> },
        { id: 'reportRate', label: 'Report Rate',defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.reportRate, align: 'right',
          render: (u) => <Box sx={{ fontWeight: 600, color: u.reportRate >= 15 ? 'var(--green)' : 'text.secondary' }}>{u.reportRate}%</Box> },
    ];

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <ActiveFilters scope={scope} removeScope={removeScope} noPadX />
            <motion.div variants={fadeUp}>
                <DataTable
                    title="High Click Users"
                    subtitle={`${rows.length} of ${baseUsers.length} users - sorted by risk score`}
                    columns={columns}
                    rows={rows}
                    rowKey={(u) => u.email || u.name}
                    onRowClick={(u) => drill('user-detail', u.name, { type: 'user', value: u.email || u.name })}
                    initialSort={{ by: 'sent', dir: 'desc' }}
                    storageKey="rbx-high-risk-cols"
                    emptyMessage="No users match the current filters"
                    headerActions={
                        <Button
                            onClick={() => exportCSV(rows)}
                            variant="outlined" size="small"
                            sx={{ borderRadius: '4px', borderColor: 'secondary.main', color: 'secondary.main', fontSize: 11, textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'secondary.main', color: 'common.white' } }}
                        >
                            Export CSV ({rows.length})
                        </Button>
                    }
                    toolbar={
                        <>
                            <FilterSelect value={filterDept}  onChange={setFilterDept}  options={depts}                  placeholder="All departments" />
                            <FilterSelect value={filterLevel} onChange={setFilterLevel} options={['high', 'med', 'low']} placeholder="All risk levels" />
                            {hasFilters && (
                                <Button size="small" onClick={() => { setFilterDept(''); setFilterLevel(''); }}
                                    sx={{ fontSize: 10, color: 'text.disabled', textTransform: 'none', minWidth: 0, px: 1, '&:hover': { color: 'text.secondary' } }}>
                                    Clear
                                </Button>
                            )}
                        </>
                    }
                />
            </motion.div>
        </motion.div>
    );
}
